"use client";

import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogBody,
} from "../../components/ui/dialog";
import Button from "../../components/button-v1";
import Input from "../../components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../components/ui/select";
import toast from "react-hot-toast";
import { getLocationList } from "../../services/admin/organization";
import { movePatients } from "../../services/patient/settings";

const MovePatientModal = ({
  isOpen,
  onClose,
  patients = [],
  fromLocation,
  onSuccess,
}) => {
  const [locations, setLocations] = useState([]);
  const [toLocationId, setToLocationId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState("");

  /* =========================
     Load Locations
  ========================= */
  useEffect(() => {
    if (!isOpen || !fromLocation) return;

    const loadLocations = async () => {
      try {
        const { locations = [] } = await getLocationList();

        // remove current location
        const filtered = locations.filter((loc) => loc.id !== fromLocation.id);

        setLocations(filtered);
        setToLocationId(null);
        setReason("");
      } catch {
        toast.error("Failed to load locations");
      }
    };

    loadLocations();
  }, [isOpen, fromLocation]);

  if (!isOpen || !patients.length || !fromLocation) return null;

  const selectedLocationName =
    locations.find((l) => String(l.id) === String(toLocationId))?.name ??
    "Select location";

  /* =========================
     Handlers
  ========================= */
  const handleMove = async () => {
    if (!toLocationId) {
      toast.error("Please select a target location");
      return;
    }

    try {
      setLoading(true);

      // Call service to move all patients
      await movePatients({
        patient_ids: patients.map((p) => p.id),
        to_location_id: toLocationId,
        current_location_id: fromLocation.id,
        reason,
      });

      toast.success(
        patients.length > 1
          ? "Patients moved successfully"
          : "Patient moved successfully"
      );

      // close and notify parent (which will invalidate queries and clear selections)
      onClose?.();
      onSuccess?.();
    } catch (error) {
      console.error("Move patients error:", error);
      // Prefer backend error message if available
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to move patient(s)";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  /* =========================
     UI
  ========================= */
  return (
    <Dialog open={isOpen} setOpen={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Move {patients.length > 1 ? "Patients" : "Patient"}
          </DialogTitle>
        </DialogHeader>

        <DialogBody>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div>
              <label style={{ fontWeight: 600 }}>Patients ({patients.length})</label>

              <div
                style={{
                  maxHeight: "140px",
                  overflowY: "auto",
                  border: "1px solid #ddd",
                  color:"#9ca3af",
                  borderRadius: "6px",
                  padding: "5px",
                  backgroundColor: "#f3f4f6",
                  fontSize: "13px",
                }}
              >
                {patients.map((p, index) => (
                  <div key={p.id} style={{ padding: "4px 0" }}>
                    {index + 1}. {p.first_name} {p.last_name}
                  </div>
                ))}
              </div>
            </div>

            {/* From Location */}
            <div>
              <label style={{ fontWeight: 600 }}>From Location</label>
              <Input value={fromLocation.name} disabled />
            </div>

            {/* To Location */}
            <div>
              <label style={{ fontWeight: 600 }}>To Location</label>
              <Select value={toLocationId} onChange={(value) => setToLocationId(value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select location">{selectedLocationName}</SelectValue>
                </SelectTrigger>

                <SelectContent searchField>
                  {locations.map((loc) => (
                    <SelectItem key={loc.id} value={String(loc.id)}>
                      {loc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Reason */}
            <div>
              <label style={{ fontWeight: 600 }}>Reason</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Reason for moving the patient(s)"
                style={{
                  width: "100%",
                  minHeight: 50,
                  padding: 8,
                  borderRadius: 6,
                  border: "1px solid #ddd",
                  resize: "vertical",
                  
                }}
              />
            </div>
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            Cancel
          </Button>

          <Button onClick={handleMove} loading={loading} disabled={loading}>
            Move {patients.length > 1 ? "Patients" : "Patient"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MovePatientModal;
